import { useState, useRef, useCallback } from "react";
import { useReduxStore } from "./useReduxStore";
import { isUserSubscribed, getItemAccessType } from "../helper/accessTypeHelper";
import adService from "../services/adService";

export const useAdGate = () => {
  const { getState } = useReduxStore();
  const { userData, token } = getState("AUTH");
  const isSubscribed = isUserSubscribed(userData);

  const [isAdModalOpen, setIsAdModalOpen] = useState(false);
  const [adData, setAdData] = useState(null);
  const [adLoading, setAdLoading] = useState(false);

  // Store the pending action to execute once the ad completes
  const pendingActionRef = useRef(null);

  /**
   * Intercepts access to a product. If the item is free and the user is NOT a VIP subscriber,
   * a randomized ad is fetched and displayed. Once the ad is completed/skipped,
   * onAccessGranted is executed.
   */
  const triggerWithAdGate = useCallback(
    async (item, onAccessGranted) => {
      // Security Guard: Unauthenticated users cannot trigger ad gates or consume content
      if (!token) {
        return;
      }

      const access = getItemAccessType(item);

      // Rule 1: Active VIP subscribers get 100% ad-free, instant access
      // Rule 2: Non-free items (Buy, PPV, Exclusive) follow their respective checkout/paywall flow
      if (isSubscribed || !access.isFree) {
        onAccessGranted?.();
        return;
      }

      // Rule 3: Free item + Non-subscribed user -> Must watch a video ad
      try {
        setAdLoading(true);
        pendingActionRef.current = onAccessGranted;

        const randomAd = await adService.getRandomAdClip();

        if (randomAd && randomAd.clip?.ad_clip) {
          setAdData(randomAd);
          setIsAdModalOpen(true);
        } else {
          // Fallback: If no ads are available or backend fails, allow content consumption
          console.warn("No ad clip available, proceeding to content.");
          onAccessGranted?.();
        }
      } catch (error) {
        console.error("AdGate error, granting fallback access:", error);
        onAccessGranted?.();
      } finally {
        setAdLoading(false);
      }
    },
    [isSubscribed, token]
  );

  const onAdCompleted = useCallback(() => {
    setIsAdModalOpen(false);
    setAdData(null);
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action();
    }
  }, []);

  const closeAdModal = useCallback(() => {
    setIsAdModalOpen(false);
    setAdData(null);
    pendingActionRef.current = null;
  }, []);

  return {
    isAdModalOpen,
    adData,
    adLoading,
    triggerWithAdGate,
    onAdCompleted,
    closeAdModal,
    isSubscribed,
  };
};

export default useAdGate;
